import React, { useState, useRef } from 'react';
import moment from 'moment-timezone';
import { CustomDatePicker } from './CustomDatePicker';
import { api } from '../utils/api';
import { getUserCompany } from '../utils/user';

interface DataAvailability {
  date: string;
  hasSleepData: boolean;
  hasVitalSignsData: boolean;
}

interface DatePickerCardProps {
  onDateChange?: (date: string) => void;
  initialDate?: string;
  deviceId?: string;
}

// Cache untuk menyimpan data availability per bulan
const availabilityCache = new Map<string, {
  data: DataAvailability[];
  timestamp: number;
}>();

const CACHE_DURATION = 5 * 60 * 1000; // 5 menit

/**
 * DatePickerCard component for date selection
 * Provides a date picker with quick selection buttons and displays the selected date
 * Shows data availability indicators for Sleep and Vital Signs data
 * Uses optimized backend aggregation API to reduce requests
 */
export const DatePickerCard: React.FC<DatePickerCardProps> = ({ 
  onDateChange,
  initialDate,
  deviceId
}) => {
  const [selectedDate, setSelectedDate] = useState<string>(
    initialDate || moment().tz('Asia/Jakarta').format('YYYY-MM-DD')
  );
  const [activeQuickButton, setActiveQuickButton] = useState<number | null>(null);
  const [dataAvailability, setDataAvailability] = useState<DataAvailability[]>([]);
  const [loading, setLoading] = useState(false);
  const loadedMonthsRef = useRef<Set<string>>(new Set());

  // Constants
  const DATE_FORMAT = 'YYYY-MM-DD';
  const DISPLAY_FORMAT = 'DD MMMM YYYY';

  // Lazy load data availability when calendar is opened
  const handleCalendarOpen = async () => {
    const currentMonth = moment(selectedDate).format('YYYY-MM');
    
    // Check cache first
    const cached = availabilityCache.get(currentMonth);
    if (cached && (Date.now() - cached.timestamp < CACHE_DURATION)) {
      console.log('Using cached availability data for', currentMonth);
      setDataAvailability(cached.data);
      return;
    }

    // Skip if already loaded this month
    if (loadedMonthsRef.current.has(currentMonth)) {
      return;
    }

    setLoading(true);
    try {
      const companyId = getUserCompany();
      
      // Call new aggregated API - single request for entire month
      const monthlyData = await api.getMonthlyDataAvailability(currentMonth, companyId);
      
      // Convert to array format for CustomDatePicker
      const availability: DataAvailability[] = Object.entries(monthlyData).map(([date, data]) => ({
        date,
        hasSleepData: data.hasSleep,
        hasVitalSignsData: data.hasVital
      }));
      
      // Cache the result
      availabilityCache.set(currentMonth, {
        data: availability,
        timestamp: Date.now()
      });
      
      loadedMonthsRef.current.add(currentMonth);
      setDataAvailability(availability);
    } catch (error) {
      console.error('Error fetching data availability:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle date change from CustomDatePicker
  const handleDateChange = (date: string) => {
    setSelectedDate(date);
    setActiveQuickButton(null);
    
    if (onDateChange) {
      onDateChange(date);
    }
  };

  // Handle quick date selection
  const handleQuickDateSelection = (days: number) => {
    const targetDate = moment().tz('Asia/Jakarta').subtract(days, 'days');
    const formattedDate = targetDate.format(DATE_FORMAT);
    
    setSelectedDate(formattedDate);
    setActiveQuickButton(days);

    // Call callback prop
    if (onDateChange) {
      onDateChange(formattedDate);
    }
  };

  // Format date for display
  const displayDate = moment(selectedDate, DATE_FORMAT).format(DISPLAY_FORMAT);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Filter Tanggal</h3>
        {loading && (
          <div className="text-sm text-gray-500">Loading data...</div>
        )}
      </div>

      <div className="space-y-4">
        {/* Date Picker Input */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <div className="relative">
            <CustomDatePicker
              onDateChange={handleDateChange}
              initialDate={selectedDate}
              deviceId={deviceId}
              dataAvailability={dataAvailability}
              onCalendarOpen={handleCalendarOpen}
            />
          </div>

          {/* Quick Date Selection */}
          <div className="grid grid-cols-2 sm:grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => handleQuickDateSelection(0)}
              className={`quick-date-btn px-1 py-2 text-xs font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors ${
                activeQuickButton === 0
                  ? 'bg-blue-500 text-white border-blue-500'
                  : 'text-gray-700 bg-gray-100 border-gray-300 hover:bg-gray-200'
              } border`}
            >
              Hari Ini
            </button>
            <button
              type="button"
              onClick={() => handleQuickDateSelection(1)}
              className={`quick-date-btn px-1 py-2 text-xs font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors ${
                activeQuickButton === 1
                  ? 'bg-blue-500 text-white border-blue-500'
                  : 'text-gray-700 bg-gray-100 border-gray-300 hover:bg-gray-200'
              } border`}
            >
              Kemarin
            </button>
          </div>

          {/* Current Selection Display */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center">
              <svg 
                className="w-4 h-4 text-blue-600 mr-2" 
                fill="currentColor" 
                viewBox="0 0 20 20"
              >
                <path 
                  fillRule="evenodd" 
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" 
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-sm text-blue-800">
                Data <span className="font-medium">{displayDate}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
